
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, MapPin, Calendar, Trophy } from "lucide-react";

const BASE_DONATION = 260;
const TEST_AMOUNT = 1;

const golfSchema = z.object({
  lastName: z.string().min(2, "Le nom doit contenir au moins 2 caractères"),
  firstName: z.string().min(2, "Le prénom doit contenir au moins 2 caractères"),
  golfIndex: z.string()
    .min(1, "Veuillez entrer votre indice de golf")
    .refine((val) => {
      const num = parseFloat(val.replace(",", "."));
      return !isNaN(num) && num >= 0 && num <= 54;
    }, "L'indice doit être un nombre entre 0 et 54"),
  teammate: z.string().optional(),
  email: z.string().email("Adresse email invalide"),
  phone: z.string().optional(),
  additionalDonation: z.string().optional(),
});

type GolfFormValues = z.infer<typeof golfSchema>;

export default function GolfInscription() {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const form = useForm<GolfFormValues>({
    resolver: zodResolver(golfSchema),
    defaultValues: {
      lastName: "",
      firstName: "",
      golfIndex: "",
      teammate: "",
      email: "",
      phone: "",
      additionalDonation: "",
    },
  });

  const onSubmit = async (data: GolfFormValues) => {
    setIsSubmitting(true);
    try {
      const additionalDonation = data.additionalDonation ? parseInt(data.additionalDonation, 10) || 0 : 0;
      const totalAmount = BASE_DONATION + additionalDonation;

      // Insert via RPC function (avoids SELECT RLS issue)
      const { data: inserted, error } = await supabase.rpc("insert_golf_registration", {
        p_first_name: data.firstName,
        p_last_name: data.lastName,
        p_email: data.email,
        p_phone: data.phone || null,
        p_golf_index: parseFloat(data.golfIndex.replace(",", ".")),
        p_teammate: data.teammate || null,
        p_amount: totalAmount,
        p_additional_donation: additionalDonation,
      });

      if (error) throw error;

      const reg = (inserted as any)?.[0] || inserted;

      // Send verification email
      const emailResult = await supabase.functions.invoke("send-golf-verification", {
        body: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          registrationId: reg.id,
          verificationToken: reg.verification_token,
          amount: totalAmount,
        },
      });

      if (emailResult.error) {
        console.error("Email error:", emailResult.error);
        // Don't block - registration is saved
      }

      setIsSubmitted(true);
      toast({
        title: "Inscription enregistrée",
        description: "Un email de confirmation vous a été envoyé.",
      });
    } catch (err: any) {
      console.error("Registration error:", err);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: err.message || "Une erreur est survenue lors de l'inscription.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background with gradient and gavroche */}
      <div className="absolute inset-0 z-0">
        <div className="h-full w-full bg-gradient-to-br from-[#FFFF4D] via-[#FFE94B] to-[#A3D960]"></div>
        <div className="absolute top-0 right-0 h-full w-1/3 md:w-2/5 opacity-30 md:opacity-40 flex items-end">
          <img 
            src="/lovable-uploads/180f51c0-05cf-4dfd-b4b7-91b153baf486.png" 
            alt="Décoration" 
            className="h-auto w-full object-contain object-bottom drop-shadow-xl" 
          />
        </div>
      </div>

      {/* Header */}
      <div className="relative z-10 bg-[#001233]/80 py-4 px-6">
        <div className="container mx-auto flex items-center gap-3">
          <Trophy className="h-8 w-8 text-[#FFFD4C]" />
          <h1 className="text-xl md:text-2xl font-bold text-white font-acumin">
            Enfants de la République
          </h1>
        </div>
      </div>

      <main className="container relative z-10 mx-auto px-4 py-8 md:py-12">
        {/* Event info */}
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-3xl md:text-5xl font-bold text-[#001233] mb-4">
            Compétition de Golf
          </h2>
          <p className="text-lg md:text-xl text-[#001233]/80 mb-6">
            Au profit de l'association Enfants de la République
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-8 text-[#001233]/90">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-[#00509E]" />
              <span className="text-lg font-medium">12 Juin 2026</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-[#00509E]" />
              <span className="text-lg font-medium">Paris International Golf (PIG)</span>
            </div>
          </div>
        </div>

        <div className="max-w-lg mx-auto">
          <Card className="border-0 shadow-2xl">
            <CardContent className="pt-6 pb-8">
              {isSubmitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-foreground mb-2"><h3 className="text-2xl font-bold text-foreground mb-2">Inscription en cours</h3></h3>
                  <p className="text-muted-foreground">
                    Un email de vérification vous a été envoyé à l'adresse indiquée.
                    Cliquez sur le lien dans l'email pour confirmer votre inscription et procéder au paiement.
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="text-xl font-bold text-center text-foreground mb-6">
                    Formulaire d'inscription
                  </h3>
                  <p className="text-sm text-muted-foreground text-center mb-4">
                    Don pour l'inscription à la compétition : <span className="font-bold text-primary">{BASE_DONATION} €</span>
                    <br />
                    <span className="font-bold">Ne vous coûtera que {(BASE_DONATION * 0.33).toFixed(2).replace(".", ",")} € après déduction fiscale</span>
                  </p>
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                      <FormField
                        control={form.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Nom *</FormLabel>
                            <FormControl>
                              <Input placeholder="Votre nom" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Prénom *</FormLabel>
                            <FormControl>
                              <Input placeholder="Votre prénom" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="golfIndex"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Indice de golf *</FormLabel>
                            <FormControl>
                              <Input
                                type="text"
                                inputMode="decimal"
                                placeholder="Ex: 18,5"
                                className="[appearance:textfield]"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="teammate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Coéquipier</FormLabel>
                            <FormControl>
                              <Input placeholder="Nom du coéquipier" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email *</FormLabel>
                            <FormControl>
                              <Input type="email" placeholder="votre@email.com" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Téléphone</FormLabel>
                            <FormControl>
                              <Input type="tel" placeholder="06 12 34 56 78" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <div className="pt-4 mt-4 border-t border-border">
                      <FormField
                        control={form.control}
                        name="additionalDonation"
                        render={({ field }) => {
                          const additionalVal = parseInt(field.value || "0", 10) || 0;
                          const totalDonation = BASE_DONATION + additionalVal;
                          const additionalAfterDeduction = (additionalVal * 0.33).toFixed(2).replace(".", ",");
                          const totalAfterDeduction = (totalDonation * 0.33).toFixed(2).replace(".", ",");
                          return (
                            <FormItem>
                              <FormLabel>Don complémentaire possible (€)</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  min="0"
                                  placeholder="0"
                                  {...field}
                                />
                              </FormControl>
                              {additionalVal > 0 && (
                                <p className="text-xs text-muted-foreground">
                                  Ne vous coûtera que {additionalAfterDeduction} € après déduction fiscale
                                </p>
                              )}
                              <FormMessage />
                              <div className="mt-3 p-4 bg-muted rounded-lg text-center">
                                <p className="text-base font-semibold text-foreground">
                                  Don total à payer : <span className="text-primary text-xl font-bold">{totalDonation} €</span>
                                </p>
                                <p className="text-sm text-muted-foreground mt-1">
                                  Ne vous coûtera que <span className="font-semibold">{totalAfterDeduction} €</span> après déduction fiscale, un CERFA vous sera envoyé.
                                </p>
                              </div>
                            </FormItem>
                          );
                        }}
                      />
                      </div>
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full py-6 bg-[#00509E] hover:bg-[#003d7a] text-white text-lg font-semibold mt-4"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            Inscription en cours...
                          </>
                        ) : (
                          "S'inscrire"
                        )}
                      </Button>
                    </form>
                  </Form>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 py-6 text-center text-[#001233]/50 text-sm">
        <p>© 2026 Enfants de la République — Compétition de golf</p>
      </footer>
    </div>
  );
}
